import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { getClient } from "services";

import {
  ChildType,
  ConnectedParentType,
  ParentType,
  PendingParentType
} from "./types";

export const ChildParentConnectionsQueryKey = "parent-connections";

export function useFetchParentConnections<TData>(
  options?: UseQueryOptions<ChildType, unknown, TData>
) {
  return useQuery<ChildType, unknown, TData>(
    [ChildParentConnectionsQueryKey],
    async () => {
      const item = await getClient("parentsService").fetchChild({
        includePending: true
      });

      return item as any;
    },
    {
      staleTime: Infinity,
      ...(options || {})
    }
  );
}

function useQuerySelector<TData>(
  selector: (data: ChildType) => any,
  otherOptions?: UseQueryOptions<ChildType, unknown, TData>
) {
  return useFetchParentConnections<TData>({
    select: selector,
    ...(otherOptions || {})
  });
}

export function useFetchAllConnections(
  options: UseQueryOptions<ChildType, unknown, ParentType[]> = {}
) {
  return useQuerySelector<ParentType[]>(allConnectionsSelector, options);
}

export function useFetchConnectedParents(
  options: UseQueryOptions<ChildType, unknown, ConnectedParentType[]> = {}
) {
  return useQuerySelector<ConnectedParentType[]>(
    connectedParentSelector,
    options
  );
}

export function useFetchPendingParentConnections(
  options: UseQueryOptions<ChildType, unknown, ParentType[]> = {}
) {
  return useQuerySelector<ParentType[]>(pendingConnectionsSelector, options);
}

export function useFetchParent(userId: string) {
  return useQuerySelector<ConnectedParentType | undefined>((data: ChildType) =>
    data.parents.find(parent => parent.userId === userId)
  );
}

const allConnectionsSelector = (data: ChildType) => data.parents;

const connectedParentSelector = (data: ChildType) =>
  data.parents.filter(isConnectedParent);

const pendingConnectionsSelector = (data: ChildType) =>
  data.parents.filter(isPendingParent);

function isConnectedParent(parent: any): parent is ConnectedParentType {
  return parent?.status === "connected";
}

function isPendingParent(parent: any): parent is PendingParentType {
  return parent?.status === "pending";
}
